import { ma } from '@lemonade-hq/chat-engine';
import type { Schema } from '@lemonade-hq/chat-engine';
import type { GenericRecordData, GenericSchemaDataValue, Shape } from '@lemonade-hq/maschema-schema';
import type { UniclientChangeValue } from '@lemonade-hq/uniclient-chat';
import deepmerge from 'deepmerge';
import get from 'lodash/get';
import set from 'lodash/set';

interface BaseItem {
  readonly id: string;
}

const isBaseItemArray = (arr: GenericRecordData): arr is BaseItem[] => {
  return (
    Array.isArray(arr) &&
    (arr.length > 0 ? arr.some(item => item != null && typeof item === 'object' && 'id' in item) : true)
  );
};

const valueIsObjectAndHasId = (value: GenericSchemaDataValue): value is BaseItem => {
  return value != null && typeof value === 'object' && 'id' in value;
};

const findIndexById = (current: BaseItem[], value?: GenericSchemaDataValue): number => {
  return current.findIndex(item => valueIsObjectAndHasId(value) && item.id === value.id);
};

export const changeValuesByStrategy = (
  data: UniclientChangeValue[],
  answers: GenericRecordData,
  answersSchema: Schema = ma.record({}),
): GenericRecordData => {
  return data.reduce((acc, item) => {
    const { value, strategy = 'replace' } = item;
    let attribute: string;

    if ('attribute' in item) {
      attribute = item.attribute;
    } else if ('uiAttribute' in item) {
      attribute = item.uiAttribute;
    } else {
      return acc;
    }

    const current = (get(answers, attribute) ?? []) as GenericRecordData;
    const attributeSchema = get(answersSchema.schema, attribute.replaceAll('.', '.schema.')) as Shape | undefined;

    switch (strategy) {
      case 'append': {
        if (attributeSchema?.type === 'map' && value != null && typeof value === 'object' && 'id' in value) {
          return set(acc, attribute, { ...current, [value.id]: value });
        }

        if (Array.isArray(current)) return set(acc, attribute, [...(current as BaseItem[]), value]);

        return acc;
      }

      case 'replace': {
        return set(acc, attribute, value);
      }

      case 'remove': {
        return set(acc, attribute, null);
      }

      case 'upsertById':
      case 'appendById': {
        if (!valueIsObjectAndHasId(value)) return acc;
        if (attributeSchema?.type === 'map' && typeof value === 'object' && 'id' in value) {
          return set(acc, attribute, deepmerge(current, { [value.id]: value }));
        }

        if (!isBaseItemArray(current)) return acc;

        const index = findIndexById(current, value);
        if (index === -1) {
          return set(acc, attribute, [...current, value]);
        }

        return set(acc, attribute, [
          ...current.slice(0, index),
          { ...current[index], ...value },
          ...current.slice(index + 1),
        ]);
      }

      case 'replaceById': {
        if (!valueIsObjectAndHasId(value)) return acc;
        if (attributeSchema?.type === 'map' && typeof value === 'object' && 'id' in value) {
          if (current[value.id] == null) return acc;

          return set(acc, attribute, { ...current, [value.id]: value });
        }

        if (!isBaseItemArray(current)) return acc;

        const index = findIndexById(current, value);
        if (index === -1) {
          return acc;
        }

        return set(acc, attribute, [...current.slice(0, index), value, ...current.slice(index + 1)]);
      }

      case 'removeById': {
        if (!valueIsObjectAndHasId(value)) return acc;
        if (attributeSchema?.type === 'map' && typeof value === 'object' && 'id' in value) {
          if (current[value.id] == null) return acc;

          const newValue = { ...current };
          // null values are removed later by chat
          newValue[value.id] = null;
          return set(acc, attribute, newValue);
        }

        if (!isBaseItemArray(current)) return acc;

        const index = findIndexById(current, value);
        if (index === -1) {
          return acc;
        }

        return set(acc, attribute, [...current.slice(0, index), ...current.slice(index + 1)]);
      }

      case 'concat': {
        if (attributeSchema?.type === 'map' && value != null && typeof value === 'object' && 'id' in value) {
          return set(acc, attribute, { ...current, [value.id]: value });
        }

        if (Array.isArray(current) && Array.isArray(value))
          return set(acc, attribute, [...(current as BaseItem[]), ...value]);

        return acc;
      }

      default: {
        return acc;
      }
    }
  }, {} as GenericRecordData);
};
